import React, { useContext, useCallback, useMemo } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { ValidatedFrequencyCapping, adGroupBidProp } from '@bingads-webui-campaign-react/xandr-campaign';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { Label } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { getBidTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { PriceModelToRange } from '../../common/constants';
import { DefaultBidWithRecommendation, useCampaignBidTypeInfo } from './bid-with-recommendation';

export const AudienceBidValue = ({
  data,
  legacyI18n,
  onDataChange,
  ceilingValue,
  floorValue,
  stopPropagation = true,
  isEdit = false,
}) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);

  const { adGroup, campaign } = data || {};

  const { BiddingScheme } = campaign;

  const onAdGroupValidate = (result) => {
    if (adGroup && result.isValid) {
      onDataChange({
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            ...result.data,
            CampaignBidStrategyName: BiddingScheme.Type,
          },
        },
        isValid: result.isValid,
      });
    }
  };

  const isFCapEnabled = useMemo(() => {
    const isCPM = BiddingScheme && BiddingScheme.Type === 'ManualCpm';
    return isCPM;
  }, [BiddingScheme]);

  const getBidRange = useCallback((type) => {
    if (!_.isUndefined(ceilingValue) && !_.isUndefined(floorValue)) {
      return {
        bidCeil: ceilingValue,
        bidFloor: floorValue,
      };
    }

    const bidCeil = _.result(serverContext[PriceModelToRange[type]], 'CeilingValue');
    const bidFloor = _.result(serverContext[PriceModelToRange[type]], 'FloorValue');

    return { bidCeil, bidFloor };
  }, [serverContext, ceilingValue, floorValue]);

  const campaignBidTypeInfo = useCampaignBidTypeInfo(campaign, serverContext.CurrentAccount.CurrencyCode);

  const CpmBid = useCallback(() => {
    const { bidCeil, bidFloor } = getBidRange(BiddingScheme.Type);

    return (
      <DefaultBidWithRecommendation
        placeholderText="25.00"
        id="input-ad-group-bid"
        currencyCode={serverContext.CurrentAccount.CurrencyCode}
        i18n={i18n}
        validationProps={{
        field: 'CpmBid',
        schema: {
          properties: {
            CpmBid: {
              type: 'number',
              minimum: bidFloor,
              maximum: bidCeil,
              required: true,
            },
          },
        },
      }}
        formErrorProps={{ filter: adGroupBidProp }}
        campaignBidTypeInfo={campaignBidTypeInfo}
        isEdit={isEdit}
      />
    );
  }, [BiddingScheme.Type, getBidRange, i18n, serverContext.CurrentAccount.CurrencyCode, campaignBidTypeInfo, isEdit]);

  const SearchBid = useCallback(() => {
    const { bidCeil, bidFloor } = getBidRange(BiddingScheme.Type);

    return (
      <DefaultBidWithRecommendation
        placeholderText="25.00"
        id="input-ad-group-bid"
        currencyCode={serverContext.CurrentAccount.CurrencyCode}
        i18n={i18n}
        validationProps={{
        field: 'SearchBid',
        schema: {
          properties: {
            SearchBid: {
              type: 'number',
              minimum: bidFloor,
              maximum: bidCeil,
              required: true,
            },
          },
        },
        }}
        formErrorProps={{ filter: adGroupBidProp }}
        campaignBidTypeInfo={campaignBidTypeInfo}
        isEdit={isEdit}
      />
    );
  }, [BiddingScheme.Type, getBidRange, i18n, serverContext.CurrentAccount.CurrencyCode, campaignBidTypeInfo, isEdit]);

  return (
    <div>
      <ValidationGroup
        validationProps={{
          schema: AdGroup,
          onValidate: onAdGroupValidate,
          data: adGroup || {},
          i18n: legacyI18n,
          stopPropagation,
        }}
      >
        <Label htmlFor="input-ad-group-bid">
          {i18n.getString(_TL_('Default bid for bid strategy: {{bidStrategy}}'), { bidStrategy: getBidTitleDetails(i18n, BiddingScheme) })}
          <HelpPopup
            topic="pop_BA_BidTypeAwareness"
            aria-label={i18n.getString(_TL_('Click to learn more about bid strategy'))}
          />
        </Label>
        {
          (BiddingScheme.Type !== 'ManualCpm') ?
            <SearchBid /> : <CpmBid />
        }
        {
          isFCapEnabled &&
          <ValidatedFrequencyCapping
            i18n={i18n}
            validationProps={{
              data: adGroup.FrequencyCapSettings || [],
            }}
          />
        }
      </ValidationGroup>
    </div>
  );
};
